import HideIcon from '@assets/icons/hide.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import RemoveCircleIcon from '@assets/icons/remove-circle.svg';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { Platform, Pressable, Text, View } from 'react-native';
import { TYPOGRAPHY } from '@/styles';
import { useLayoutContext } from '@/contexts/layout-context';

interface Props {
  onClose: () => void;
  onDeleteAlert: () => void;
  onHideAlert?: () => void;
  targetRef: React.RefObject<View | null> | undefined;
}

/**
 * Pressable typehints from React Native are lacking, and have bad support for RN for web, even though they do expose focused and hovered
 * This adds typing to make typescript happy
 * ref: https://github.com/necolas/react-native-web/issues/1708#issuecomment-728777256
 */
export type PressableState = Readonly<{
  focused?: boolean;
  hovered?: boolean;
  pressed: boolean;
}>;

export const InsightsHubActionSheet: React.FC<Props> = ({
  onClose,
  onDeleteAlert,
  onHideAlert,
  targetRef,
}) => {
  const [styles] = useStyles();
  const { isSmallLayout } = useLayoutContext();

  return (
    <BaseActionSheet
      contentStyle={
        Platform.OS !== 'web' ? styles.actionSheetResetPadding : null
      }
      isVisible
      onClose={onClose}
      popoverStyle={(_, targetPosition) => {
        if (Platform.OS !== 'web') {
          return;
        }
        const POPOVER_WIDTH = 120;
        return {
          left: targetPosition.x - (POPOVER_WIDTH - targetPosition.width),
          minWidth: 1,
          padding: 0,
          top: targetPosition.height + targetPosition.y,
        };
      }}
      popoverTarget={targetRef?.current}
    >
      {onHideAlert !== undefined ? (
        <Pressable
          onPress={onHideAlert}
          style={({ hovered, pressed }: PressableState) => [
            styles.row,
            !isSmallLayout ? styles.desktopRow : null,
            hovered ? styles.hover : null,
            pressed ? styles.pressed : null,
          ]}
        >
          <HideIcon
            height={isSmallLayout ? 25 : 16}
            width={isSmallLayout ? 24 : 16}
          />
          <Text
            style={[
              TYPOGRAPHY.h3,
              isSmallLayout ? styles.title : styles.desktopTitle,
            ]}
          >
            {i18n.t('insights_hub.action_sheet.hide')}
          </Text>
        </Pressable>
      ) : null}
      <Pressable
        onPress={onDeleteAlert}
        style={({ hovered, pressed }: PressableState) => [
          styles.row,
          !isSmallLayout ? styles.desktopRow : null,
          hovered ? styles.hover : null,
          pressed ? styles.pressed : null,
        ]}
      >
        <RemoveCircleIcon
          height={isSmallLayout ? 25 : 16}
          width={isSmallLayout ? 24 : 16}
        />
        <Text
          style={[
            TYPOGRAPHY.h3,
            isSmallLayout ? styles.title : styles.desktopTitle,
            styles.titleDestructive,
          ]}
        >
          {i18n.t('remove')}
        </Text>
      </Pressable>
    </BaseActionSheet>
  );
};

const useStyles = createStyles((theme) => ({
  actionSheetResetPadding: {
    paddingTop: 24,
  },
  desktopRow: {
    gap: 12,
    paddingLeft: 16,
    paddingRight: 24,
    paddingVertical: 8,
  },
  desktopTitle: {
    color: theme.sk.color.text.primary,
    fontFamily: theme.fonts.primary,
    fontSize: 14,
  },
  hover: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  pressed: {
    backgroundColor: theme.sk.color.bg.primary.pressed,
    opacity: 0.2,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 16,
    paddingHorizontal: 8,
    paddingVertical: 16,
  },
  title: {
    color: theme.sk.color.text.primary,
    fontSize: 16,
  },
  titleDestructive: {
    color: theme.sk.color.text.error,
  },
}));
